import { batch } from 'editor/src/store/batchedSubscribeEnhancer';
import getMediaElementByUuid from 'editor/src/store/design/selector/getMediaElementByUuid';
import getSharedTextFieldTargets from 'editor/src/store/design/selector/getSharedTextFieldTargets';
import getStructureIndexByElementUuid from 'editor/src/store/design/selector/getStructureIndexByElementUuid';
import { MediaText } from 'editor/src/store/design/types';
import { getFieldKeyMembers } from 'editor/src/store/design/util/fieldKeys';
import type { Thunk } from 'editor/src/store/hooks';

import updateMediaElementOperation, { MediaUpdateActionName } from './updateMediaElementOperation';
import updateSharedTextFieldOperation from './updateSharedTextFieldOperation';

/**
 * Says which host field fills this text ("Filled from: Deceased name"), or that
 * none does when `fieldKey` is undefined.
 *
 * Choosing a field another text already uses also links the two, as "Share this
 * value with another layer" would: the host fills both either way, but only a
 * linked pair stays in step when the customer then corrects the name on one page.
 * Texts that cannot be linked (locked, per-option, outside the customer's fields)
 * just get the key. Clearing the key leaves an existing link alone.
 */
const setTextFieldKeyOperation =
  (elementUuid: number, fieldKey: string | undefined): Thunk =>
  (dispatch, getState) => {
    const element = getMediaElementByUuid(getState(), elementUuid) as MediaText | undefined;
    if (!element || element.type !== 'text' || element.extra.fieldKey === fieldKey) {
      return;
    }

    batch(() => {
      if (fieldKey) {
        const state = getState();
        const joinable = new Set(
          getSharedTextFieldTargets(state)
            .filter((target) => target.canJoin && !target.variesByPicklist)
            .map((target) => target.uuid),
        );
        const ownGroup = element.extra.sharedFieldId;
        const partner = getFieldKeyMembers(state.design.designData, fieldKey).find(
          ({ element: other }) => other.uuid !== elementUuid && !(ownGroup && other.extra.sharedFieldId === ownGroup),
        );
        if (partner && joinable.has(elementUuid) && joinable.has(partner.element.uuid)) {
          dispatch(updateSharedTextFieldOperation(elementUuid, partner.element.uuid));
        }
      }

      // Re-read: joining rewrites the element's extra (group id, adopted text).
      const state = getState();
      const current = getMediaElementByUuid(state, elementUuid) as MediaText | undefined;
      const address = getStructureIndexByElementUuid(state, elementUuid);
      if (!current || !address) {
        return;
      }
      dispatch(
        updateMediaElementOperation(
          address,
          { extra: { ...current.extra, fieldKey } },
          MediaUpdateActionName.TEXT_UPDATED,
          true,
        ),
      );
    });
  };

export default setTextFieldKeyOperation;
